import React, { useRef } from 'react';
import styled from 'styled-components';
import { motion, useInView } from 'framer-motion';
import { FaEnvelope, FaMapMarkerAlt, FaGithub, FaLinkedin } from 'react-icons/fa';

const Contact = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.1 });

  const fadeInVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <ContactSection id="contact" ref={ref}>
      <div className="container">
        <SectionHeader
          variants={fadeInVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          transition={{ duration: 0.6 }}
        >
          <h2>Get In Touch</h2>
          <Divider />
        </SectionHeader>

        <ContactContent>
          <ContactInfoCenter
            variants={fadeInVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h3>Contact Information</h3>
            <ContactInfoItem>
              <IconWrapper>
                <FaEnvelope />
              </IconWrapper>
              <div>
                <h4>Email</h4>
                <a href="mailto:amritphuyal019@gmail.com">amritphuyal019@gmail.com</a>
              </div>
            </ContactInfoItem>
            {/* Phone section removed for privacy */}
            <ContactInfoItem>
              <IconWrapper>
                <FaMapMarkerAlt />
              </IconWrapper>
              <div>
                <h4>Location</h4>
                <p>Kathmandu, Nepal GMT +5:45</p>
              </div>
            </ContactInfoItem>

            <SocialLinks>
              <SocialLink href="https://github.com/amritphuyal" target="_blank" rel="noopener noreferrer">
                <FaGithub />
              </SocialLink>
              <SocialLink href="https://www.linkedin.com/in/amritphuyal/" target="_blank" rel="noopener noreferrer">
                <FaLinkedin />
              </SocialLink>
            </SocialLinks>

            <ContactNote>
              Feel free to reach out through email or social media. I'll get back to you as soon as possible!
            </ContactNote>
          </ContactInfoCenter>
        </ContactContent>
      </div>
    </ContactSection>
  );
};

// Styled Components
const ContactSection = styled.section`
    padding: 100px 0;
    background-color: var(--primary);

    .container {
        max-width: 1200px;
        margin: 0 auto;
        padding: 0 2rem;
    }
`;

const SectionHeader = styled(motion.div)`
    margin-bottom: 60px;
    text-align: center;

    h2 {
        font-size: 2.5rem;
        color: var(--text-light);
        margin-bottom: 1rem;
        position: relative;
        display: inline-block;
    }
`;

const Divider = styled.div`
    height: 4px;
    width: 70px;
    background-color: var(--accent);
    margin: 0 auto;
`;

const ContactContent = styled.div`
    display: flex;
    justify-content: center;
`;

const ContactInfoCenter = styled(motion.div)`
    background-color: var(--secondary);
    padding: 40px;
    border-radius: 6px;
    box-shadow: 0 10px 30px -15px var(--shadow);
    max-width: 600px;
    width: 100%;

    h3 {
        color: var(--text-light);
        font-size: 1.8rem;
        margin-bottom: 30px;
        position: relative;
        padding-bottom: 10px;
        text-align: center;
        
        &:after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 50%;
            transform: translateX(-50%);
            height: 2px;
            width: 70px;
            background-color: var(--accent);
        }
    }
`;

const ContactInfoItem = styled.div`
    display: flex;
    margin-bottom: 25px;
    
    h4 {
        color: var(--text-light);
        font-size: 1.1rem;
        margin-bottom: 5px;
    }

    p, a {
        color: var(--text-secondary);
        font-size: 1rem;
        text-decoration: none;
        transition: color 0.3s ease;
    }

    a:hover {
        color: var(--accent);
        text-decoration: underline;
        text-underline-offset: 3px;
    }
`;

const IconWrapper = styled.div`
    width: 40px;
    height: 40px;
    background-color: rgba(30, 136, 229, 0.1);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 15px;
    color: var(--accent);
    font-size: 1.2rem;
    transition: all 0.3s ease;
    
    ${ContactInfoItem}:hover & {
        transform: scale(1.1);
        background-color: rgba(30, 136, 229, 0.2);
    }
`;

const SocialLinks = styled.div`
    display: flex;
    margin-top: 30px;
    gap: 15px;
    justify-content: center;
`;

const SocialLink = styled.a`
    width: 40px;
    height: 40px;
    background-color: rgba(30, 136, 229, 0.1);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--accent);
    font-size: 1.2rem;
    transition: all 0.3s ease;
    
    &:hover {
        background-color: var(--accent);
        color: var(--primary);
        transform: translateY(-3px);
    }
`;

const ContactNote = styled.p`
  margin-top: 30px;
  text-align: center;
  color: var(--text-secondary);
  font-size: 1.1rem;
  line-height: 1.6;
  padding-top: 20px;
  border-top: 1px dashed rgba(30, 136, 229, 0.3);
`;

export default Contact;
